import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Renders a stored emoji value — either a unicode char ("🎮") or a custom
 * emoji reference in "name:id" form. For custom refs, resolves to the CDN
 * PNG/GIF via the /api/emojis directory (so animated emojis animate live).
 * Falls back to a raw text rendering if the id isn't in the directory yet
 * (e.g. the bot lost membership in the source guild).
 */

export interface EmojiEntry {
  id: string;
  name: string;
  animated: boolean;
  imageUrl: string;
  guildId: string;
  guildName: string;
  guildIconUrl: string | null;
  identifier: string;
  available: boolean;
}

interface EmojiDirectory {
  emojis: EmojiEntry[];
  fetchedAt: string;
  guilds: Array<{ id: string; name: string; iconUrl: string | null; emojiCount: number }>;
}

export function useEmojiDirectory() {
  return useQuery({
    queryKey: ['emojis-all'],
    queryFn: () => api<EmojiDirectory>('/api/emojis'),
    staleTime: 5 * 60_000,
  });
}

/** Split "name:id" into {name, id}; returns null for plain unicode. */
export function parseCustomEmoji(raw: string | null | undefined) {
  if (!raw) return null;
  const m = raw.match(/^([\w-]+):(\d+)$/);
  return m ? { name: m[1], id: m[2] } : null;
}

interface Props {
  value: string | null | undefined;
  size?: number;
  className?: string;
}

export function EmojiDisplay({ value, size = 20, className }: Props) {
  const parsed = parseCustomEmoji(value);
  const dir = useEmojiDirectory();

  if (!value) {
    return <span className={cn('text-muted-foreground', className)}>—</span>;
  }
  if (!parsed) {
    // Plain unicode.
    return (
      <span
        className={cn('inline-block leading-none align-middle', className)}
        style={{ fontSize: size }}
      >
        {value}
      </span>
    );
  }

  const known = dir.data?.emojis.find((e) => e.id === parsed.id);
  const url =
    known?.imageUrl ??
    `https://cdn.discordapp.com/emojis/${parsed.id}.${
      known?.animated ? 'gif' : 'png'
    }`;
  return (
    <img
      src={url}
      alt={parsed.name}
      title={known ? `:${known.name}: from ${known.guildName}` : `:${parsed.name}:`}
      className={cn('inline-block align-middle', className)}
      style={{ height: size, width: size }}
      loading="lazy"
    />
  );
}
